import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const sections = [
  {
    num: 1,
    title: 'Information We Collect',
    content: (
      <>
        <p>To provide our personalised Vedic astrology services, Taraanaya Vedtech Private Limited (the &ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects the following categories of personal data:</p>
        <ul>
          <li><strong>Birth Details:</strong> Full name, date of birth, time of birth and place of birth. Used solely to compute your personalised Vedic chart.</li>
          <li><strong>Account Information:</strong> Email address, mobile number and Google / Apple sign-in identifier.</li>
          <li><strong>Usage &amp; Device Data:</strong> App-interaction events, device model, operating-system version, app version and crash logs &mdash; used to improve the service and resolve bugs.</li>
          <li><strong>Payment Information:</strong> Transaction identifiers and order metadata only. Card numbers, UPI handles, bank details and CVVs are processed by certified payment partners (Razorpay on web, Google Play Billing on Android, Apple IAP on iOS) and are never stored on our servers.</li>
          <li><strong>Communications:</strong> Support emails and Sage-AI question history that you voluntarily submit.</li>
        </ul>
      </>
    )
  },
  {
    num: 2,
    title: 'How We Use Your Information',
    content: (
      <ul>
        <li>To deliver the personalised astrology services you have purchased &mdash; including life-story videos, Future Reels, domain insights and Sage AI answers.</li>
        <li>To process payments, issue invoices and prevent fraudulent transactions.</li>
        <li>To send transactional notifications, receipts and one-time passwords (OTPs).</li>
        <li>To improve our prediction algorithms, fix defects and ensure platform safety.</li>
        <li>With your explicit consent, to send marketing or product-update emails &mdash; you can unsubscribe at any time.</li>
        <li>To comply with applicable laws, court orders and regulatory requests in India.</li>
      </ul>
    )
  },
  {
    num: 3,
    title: 'Data Storage &amp; Security',
    content: (
      <p>Your data is stored on encrypted cloud infrastructure located in India / Asia and is protected with industry-standard controls including TLS 1.2+ encryption in transit, encryption at rest, role-based access controls, audit logs and periodic security reviews. Birth-chart inputs and personal data are <strong>never</strong> sold or shared with third parties for marketing.</p>
    )
  },
  {
    num: 4,
    title: 'Third-Party Services',
    content: (
      <>
        <p>We rely on a small set of carefully chosen processors:</p>
        <ul>
          <li><strong>Razorpay Software Private Limited</strong> &mdash; web payment processing (PCI-DSS Level 1).</li>
          <li><strong>Google LLC / Google Play Billing</strong> &mdash; Android in-app payment processing and authentication.</li>
          <li><strong>Apple Inc. / In-App Purchase</strong> &mdash; iOS in-app payment processing and authentication.</li>
          <li><strong>BillDesk (IndiaIdeas.com Limited)</strong> &mdash; KYC compliance partner for Google Play Billing in India per RBI Payment Aggregator regulations.</li>
          <li><strong>OpenAI / Anthropic / Google AI</strong> &mdash; large-language-model providers used for astrology interpretation and Sage-AI answers (no birth-chart raw data is used to train their models).</li>
          <li><strong>Cloud hosting providers</strong> &mdash; for encrypted database storage, file hosting and content delivery.</li>
          <li><strong>Analytics partners</strong> &mdash; Microsoft Clarity, Meta Pixel, PostHog and Google Analytics (aggregated, anonymised data only).</li>
        </ul>
        <p>Each processor is bound by a written data-processing agreement that restricts how your data may be used.</p>
      </>
    )
  },
  {
    num: 5,
    title: 'Your Rights (DPDP Act, 2023)',
    content: (
      <>
        <p>Under India&rsquo;s Digital Personal Data Protection Act, 2023, you have the following rights:</p>
        <ul>
          <li><strong>Right of access:</strong> view a summary of your data within the app or on request.</li>
          <li><strong>Right to correction:</strong> update your birth details, name and email at any time from your profile.</li>
          <li><strong>Right to erasure:</strong> request complete deletion of your account and associated personal data by writing to <a href="mailto:taraanaya@taraanaya.ai" style={{ color: '#74B3FF' }}>taraanaya@taraanaya.ai</a>; we will action the request within 30 days.</li>
          <li><strong>Right to data portability:</strong> request a machine-readable copy of your data.</li>
          <li><strong>Right to withdraw consent:</strong> for marketing communications and analytics, at any time.</li>
          <li><strong>Right to grievance redressal:</strong> reach our Grievance Officer (Section 9 below) for any DPDP-related concern.</li>
        </ul>
      </>
    )
  },
  {
    num: 6,
    title: 'Children &amp; Minors',
    content: (
      <p>Taraanaya is not intended for children under the age of 13. Users between 13 and 18 years of age may use the free preview only with verifiable parental or guardian consent, in line with the Digital Personal Data Protection Act, 2023. We do not knowingly collect data from anyone below 13. If you believe we have inadvertently collected such data, write to us at <a href="mailto:taraanaya@taraanaya.ai" style={{ color: '#74B3FF' }}>taraanaya@taraanaya.ai</a> and we will delete it promptly.</p>
    )
  },
  {
    num: 7,
    title: 'Subscriptions, Billing &amp; Refunds',
    content: (
      <>
        <p>Taraanaya offers two product categories &mdash; <strong>30-day Subscription Plans</strong> (Starter ₹199, Premium ₹599, Ultimate ₹999) and <strong>Sage AI Question Credit Packs</strong> (Small ₹199, Medium ₹599, Large ₹999). All prices are in Indian Rupees (INR), inclusive of 18% GST (SAC 998599).</p>
        <ul>
          <li><strong>Web (taraanaya.ai):</strong> Subscriptions are non-auto-renewing &mdash; you renew manually.</li>
          <li><strong>Android (Google Play):</strong> Subscriptions auto-renew unless cancelled in your Google Play account at least 24 hours before the next billing cycle.</li>
          <li><strong>iOS (Apple App Store):</strong> Subscriptions auto-renew unless cancelled in your Apple ID subscription settings at least 24 hours before the next billing cycle.</li>
          <li><strong>Refunds:</strong> 7-day full refund on subscription plans if the personalised content has not been streamed. Credit packs are non-refundable once credits are added to your wallet. Detailed terms are in our <a href="/refund-policy" style={{ color: '#74B3FF' }}>Refund &amp; Cancellation Policy</a>.</li>
        </ul>
      </>
    )
  },
  {
    num: 8,
    title: 'Disclaimer',
    content: (
      <p>Taraanaya provides Vedic astrology content for personal entertainment, reflection and decision-support. Predictions are <strong>not</strong> medical, legal, financial or psychiatric advice and should not be used as the sole basis for important life decisions. We make no guarantee that any predicted outcome will materialise.</p>
    )
  },
  {
    num: 9,
    title: 'Grievance Officer &amp; Contact',
    content: (
      <>
        <p>For any privacy-related question, data-deletion request or grievance under the Digital Personal Data Protection Act, 2023, please contact:</p>
        <p style={{ marginTop: 8 }}>
          <strong>Mr. Mandeep Singh Chadha</strong><br />
          Director &amp; Grievance Officer<br />
          Taraanaya Vedtech Private Limited<br />
          C-3/101, 1st Floor, Tower C3, Cleo County, Sector 121,<br />
          Noida, Gautam Buddha Nagar, Uttar Pradesh &ndash; 201301, India<br />
          Email: <a href="mailto:taraanaya@taraanaya.ai" style={{ color: '#74B3FF' }}>taraanaya@taraanaya.ai</a><br />
          Working hours: Monday &ndash; Saturday, 10:00 AM &ndash; 7:00 PM IST
        </p>
        <p style={{ marginTop: 8 }}>We acknowledge grievances within 48 working hours and resolve them within 30 days as required by law.</p>
      </>
    )
  },
  {
    num: 10,
    title: 'Governing Law &amp; Updates',
    content: (
      <p>This policy is governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts of Gautam Buddha Nagar, Uttar Pradesh. We may update this policy from time to time; the &ldquo;last updated&rdquo; date at the top reflects the most recent version. Continued use of the service after an update constitutes acceptance of the revised policy.</p>
    )
  }
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="privacy-policy-page" style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at top, #1a2332 0%, #0a0e17 60%)',
      color: 'rgba(255,255,255,0.85)',
      overscrollBehavior: 'none'
    }}>
      <div style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: '20px',
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
        paddingBottom: '60px'
      }}>
        {/* Back Button */}
        <button
          data-testid="privacy-back-btn"
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '28px',
            padding: '8px 16px',
            borderRadius: '20px',
            transition: 'all 0.2s ease'
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: 'clamp(26px, 5vw, 34px)',
            color: '#ffffff',
            fontWeight: 700,
            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            letterSpacing: '-0.3px',
            margin: 0
          }}>
            Privacy Policy
          </h1>
          <p style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.45)',
            marginTop: '6px',
            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
          }}>Last updated: 20 September 2026</p>
        </div>

        {/* Company Identity Block */}
        <div style={{
          background: 'rgba(116, 179, 255, 0.05)',
          border: '1px solid rgba(116, 179, 255, 0.18)',
          borderRadius: '12px',
          padding: '16px 18px',
          marginBottom: '24px',
          fontSize: '14px',
          lineHeight: 1.65,
          color: 'rgba(255,255,255,0.78)',
          fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
        }}>
          This Privacy Policy applies to the services offered by{' '}
          <strong style={{ color: '#ffffff' }}>Taraanaya Vedtech Private Limited</strong>
          {' '}on the website <strong>www.taraanaya.ai</strong> and the Taraanaya Android &amp; iOS mobile applications.
          <br />
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
            CIN: U96906UW2026PTC249734 &nbsp;|&nbsp; Registered with Central Registration Centre (RoC, MCA)<br />
            Registered Office: C-3/101, 1st Floor, Tower C3, Cleo County, Sector 121,<br />
            Noida, Gautam Buddha Nagar, Uttar Pradesh &ndash; 201301, India.
          </span>
        </div>

        {/* Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {sections.map((section) => (
            <section
              key={section.num}
              data-testid={`privacy-section-${section.num}`}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '12px',
                padding: '20px 22px'
              }}
            >
              <h2
                style={{
                  fontSize: 'clamp(15px, 3.8vw, 17px)',
                  color: '#ffffff',
                  fontWeight: 600,
                  margin: 0,
                  marginBottom: '12px',
                  fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
                }}
                dangerouslySetInnerHTML={{ __html: `${section.num}. ${section.title}` }}
              />
              <div style={{
                fontSize: '15px',
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.72)',
                fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
              }}>
                <style>{`
                  .pp-content ul { padding-left: 20px; margin-top: 8px; margin-bottom: 0; }
                  .pp-content li { margin-bottom: 6px; }
                  .pp-content li strong { color: rgba(255,255,255,0.92); }
                  .pp-content p { margin: 0 0 8px 0; }
                  .pp-content p:last-child { margin-bottom: 0; }
                `}</style>
                <div className="pp-content">{section.content}</div>
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255,255,255,0.06)'
        }}>
          <p style={{
            fontSize: '12px',
            color: 'rgba(255,255,255,0.45)',
            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            lineHeight: 1.65
          }}>
            &copy; 2026 Taraanaya Vedtech Private Limited. All rights reserved.<br />
            CIN: U96906UW2026PTC249734 &nbsp;|&nbsp;{' '}
            <a href="mailto:taraanaya@taraanaya.ai" style={{ color: 'rgba(116,179,255,0.8)', textDecoration: 'none' }}>taraanaya@taraanaya.ai</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
